import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { filterFiles } from '@utils/files';
import { MAX_UPLOAD_BYTES } from '@constants/index';

interface UseFileAttachmentsOptions {
  /** Server-enforced ceiling on attachments per message. */
  maxFiles: number;
}

export interface UseFileAttachmentsResult {
  pendingFiles: File[];
  fileError: string | null;
  /** Slots left before {@link UseFileAttachmentsOptions.maxFiles} is reached. */
  remainingSlots: number;
  handleFileSelect: (files: FileList | File[] | null) => void;
  handleRemoveFile: (index: number) => void;
  clearFiles: () => void;
}

/**
 * Manages pending file state and validation for chat/prompt inputs.
 * Shared by ChatInputArea and TaskPrompt.
 */
export function useFileAttachments({ maxFiles }: UseFileAttachmentsOptions): UseFileAttachmentsResult {
  const intl = useIntl();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = useCallback(
    (files: FileList | File[] | null) => {
      if (!files || files.length === 0) return;
      const { valid, oversized } = filterFiles(Array.from(files), MAX_UPLOAD_BYTES);
      const accepted = valid.slice(0, Math.max(0, maxFiles - pendingFiles.length));

      if (accepted.length < valid.length) {
        setFileError(
          intl.formatMessage(
            {
              id: 'chat.error.tooManyAttachments',
              defaultMessage:
                'A message can carry at most {max, plural, one {# attachment} other {# attachments}}.',
            },
            { max: maxFiles },
          ),
        );
      } else if (oversized.length > 0) {
        setFileError(
          intl.formatMessage(
            {
              id: 'chat.error.fileTooLarge',
              defaultMessage: '"{name}" exceeds the 500 MB limit and cannot be uploaded.',
            },
            { name: oversized[0].name },
          ),
        );
      } else {
        setFileError(null);
      }

      if (accepted.length > 0) {
        setPendingFiles((prev) => [...prev, ...accepted]);
      }
    },
    [intl, maxFiles, pendingFiles.length],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const clearFiles = useCallback(() => {
    setPendingFiles([]);
    setFileError(null);
  }, []);

  return {
    pendingFiles,
    fileError,
    remainingSlots: Math.max(0, maxFiles - pendingFiles.length),
    handleFileSelect,
    handleRemoveFile,
    clearFiles,
  };
}
